import React from "react";
import {
  Checkbox,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DiscoveredCamera } from "./OnvifScanUtils/onvifScanInterfaces";

const headerSx = { fontFamily: "Mulish", fontWeight: 700, color: "#001943" };
const cellSx = { fontFamily: "Mulish", color: "#283E6D" };

const AMBIGUOUS_RESOLUTION_HINT =
  "This device reported the same encoder settings for every stream, or a " +
  "resolution its own encoder options exclude. The resolutions below are not " +
  "reliable — pick the stream by its main/sub role and RTSP path.";

const effectiveProfileToken = (
  camera: DiscoveredCamera,
  selectedProfiles: Record<string, string>
): string =>
  selectedProfiles[camera.key] ||
  camera.profileToken ||
  camera.profiles[0]?.token ||
  "";

export const OnvifResultsTable = ({
  cameras,
  selectedKeys,
  selectedProfiles,
  onToggle,
  onToggleAll,
  onProfileChange,
}: {
  cameras: DiscoveredCamera[];
  selectedKeys: Record<string, boolean>;
  selectedProfiles: Record<string, string>;
  onToggle: (key: string) => void;
  onToggleAll: (checked: boolean) => void;
  onProfileChange: (key: string, token: string) => void;
}) => {
  const selectedCount = cameras.filter((c) => selectedKeys[c.key]).length;
  const allSelected = cameras.length > 0 && selectedCount === cameras.length;
  const someSelected = selectedCount > 0 && selectedCount < cameras.length;

  return (
    <TableContainer component={Paper} sx={{ maxHeight: "50vh" }}>
      <Table stickyHeader size="small" aria-label="discovered cameras">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
                inputProps={{ "aria-label": "select all discovered cameras" }}
              />
            </TableCell>
            <TableCell sx={headerSx}>IP</TableCell>
            <TableCell sx={headerSx}>Channel</TableCell>
            <TableCell sx={headerSx}>Manufacturer</TableCell>
            <TableCell sx={headerSx}>Model</TableCell>
            <TableCell sx={headerSx}>Stream</TableCell>
            <TableCell sx={headerSx}>RTSP URL</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cameras.map((camera) => {
            const token = effectiveProfileToken(camera, selectedProfiles);
            const activeProfile =
              camera.profiles.find((p) => p.token === token) ||
              camera.profiles[0];
            const rtspUri = activeProfile?.rtspUri || camera.rtspUri;
            return (
              <TableRow
                key={camera.key}
                hover
                onClick={() => onToggle(camera.key)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={!!selectedKeys[camera.key]}
                    onChange={() => onToggle(camera.key)}
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{ "aria-label": `select camera ${camera.ip}` }}
                  />
                </TableCell>
                <TableCell sx={cellSx}>{camera.ip}</TableCell>
                <TableCell sx={cellSx}>
                  {camera.channel == null ? "—" : camera.channel}
                </TableCell>
                <TableCell sx={cellSx}>{camera.manufacturer || "—"}</TableCell>
                <TableCell sx={cellSx}>{camera.model || "—"}</TableCell>
                {/* Always a picker, even for a single stream: which stream gets
                    onboarded must be visible and deliberate, not inferred. */}
                <TableCell sx={cellSx}>
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <Select
                      value={token}
                      onChange={(e) => onProfileChange(camera.key, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontFamily: "Mulish", fontSize: 13 }}
                    >
                      {camera.profiles.map((p) => (
                        <MenuItem
                          key={p.token}
                          value={p.token}
                          sx={{ fontFamily: "Mulish", fontSize: 13 }}
                        >
                          {p.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {camera.resolutionsAmbiguous && (
                    <Tooltip title={AMBIGUOUS_RESOLUTION_HINT}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          marginTop: 4,
                          color: "#B26A00",
                          fontSize: 11,
                        }}
                      >
                        <WarningAmberIcon sx={{ fontSize: 14 }} />
                        Reported resolutions unreliable
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ ...cellSx, maxWidth: 360 }}>
                  <Tooltip title={rtspUri}>
                    <span
                      style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {rtspUri}
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
